/* SolicitorPortalPage.jsx — Calm public operational portal for solicitors.
 *
 * Token-scoped, no auth required. Surfaces the calm operational view
 * for a single transaction: stage, milestones, outstanding documents,
 * open coordination items, recent timeline, and three lightweight
 * actions (acknowledge, confirm document, report blocker).
 *
 * Doctrine:
 *   - No sidebar. No agency internals. No fees. No scores.
 *   - Mobile-first. Single calm scroll.
 *   - Tone matches the rest of ChainFlow: composed, professional,
 *     never marketing, never gamified.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, FileCheck2, AlertCircle, Loader2, ChevronRight,
  Building2, ListChecks, Sparkles, Clock, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const STORAGE_TOKEN_KEY = "chainflow:solicitor-portal-token";

const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const portalClient = axios.create({
  baseURL: apiBase,
  withCredentials: false,
});

const cardStyle = {
  borderRadius: "20px",
  border: "1px solid rgba(226,232,240,0.95)",
  background: "#FFFFFF",
  boxShadow: "0 10px 30px -20px rgba(15,23,42,0.18)",
};

const eyebrowStyle =
  "text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700";

const PARTICIPANT_LABEL = {
  buyer:           "Buyer",
  seller:          "Seller",
  buyer_solicitor: "Buyer's solicitor",
  seller_solicitor:"Seller's solicitor",
  mortgage_broker: "Mortgage broker",
  negotiator:      "Negotiator",
  progressor:      "Sales progressor",
  other:           "Coordinator",
};

const relativeFromNow = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "moments ago";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short",
  });
};

const formatDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString("en-GB", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return s;
  }
};

export default function SolicitorPortalPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const initialToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("token");
    if (q) return q;
    try {
      return window.sessionStorage.getItem(STORAGE_TOKEN_KEY) || "";
    } catch {
      return "";
    }
  }, [location.search]);

  const [token, setToken] = useState(initialToken);
  const [loading, setLoading] = useState(!!initialToken);
  const [error, setError] = useState(null);
  const [view, setView] = useState(null);
  const [busyAck, setBusyAck] = useState(false);
  const [acked, setAcked] = useState(false);
  const [busyBlock, setBusyBlock] = useState(false);
  const [blockerText, setBlockerText] = useState("");
  const [docBusyId, setDocBusyId] = useState(null);

  // Determine if path is the "access" landing — read token from query and
  // redirect into the calm portal scroll. Otherwise this IS the portal scroll.
  const isAccessPath = location.pathname.endsWith("/access");

  useEffect(() => {
    if (!initialToken) {
      setLoading(false);
      setError("This portal link is missing or has expired.");
      return;
    }
    setToken(initialToken);
    try { window.sessionStorage.setItem(STORAGE_TOKEN_KEY, initialToken); } catch { /* noop */ }
    if (isAccessPath) {
      // Validate the token, then redirect to the portal proper.
      (async () => {
        try {
          await portalClient.post("/api/solicitor-portal/access", { token: initialToken });
          navigate("/solicitor-portal", { replace: true });
        } catch (err) {
          const detail = err?.response?.data?.detail || "This portal link is invalid.";
          setError(detail);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [initialToken, isAccessPath, navigate]);

  const fetchView = useCallback(async (t) => {
    if (!t) return;
    setLoading(true);
    try {
      const res = await portalClient.get("/api/solicitor-portal/view", {
        params: { token: t },
      });
      setView(res.data || null);
      setError(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || "This portal link is invalid.";
      setView(null);
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAccessPath) return;          // access page validates separately
    if (!token) {
      setLoading(false);
      setError("This portal link is missing or has expired.");
      return;
    }
    fetchView(token);
  }, [token, isAccessPath, fetchView]);

  const handleAcknowledge = async () => {
    if (busyAck) return;
    setBusyAck(true);
    try {
      await portalClient.post("/api/solicitor-portal/acknowledge", {}, {
        headers: { "X-Portal-Token": token },
      });
      setAcked(true);
      toast.success("Thanks — the instructing agent has been notified.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record acknowledgment.");
    } finally {
      setBusyAck(false);
    }
  };

  const handleBlocker = async () => {
    if (busyBlock || blockerText.trim().length < 3) return;
    setBusyBlock(true);
    try {
      await portalClient.post(
        "/api/solicitor-portal/blocker",
        { text: blockerText.trim().slice(0, 280) },
        { headers: { "X-Portal-Token": token } },
      );
      setBlockerText("");
      toast.success("Logged. The instructing agent has been notified.");
      await fetchView(token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record blocker.");
    } finally {
      setBusyBlock(false);
    }
  };

  const handleConfirmDoc = async (request) => {
    if (docBusyId) return;
    setDocBusyId(request.id);
    try {
      await portalClient.post(
        "/api/solicitor-portal/document-confirm",
        { request_id: request.id, note: "Confirmed via portal" },
        { headers: { "X-Portal-Token": token } },
      );
      toast.success(`${request.doc_type || "Document"} marked as ready.`);
      await fetchView(token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not confirm document.");
    } finally {
      setDocBusyId(null);
    }
  };

  if (isAccessPath || loading) {
    return (
      <PortalShell>
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="animate-spin text-violet-500 mb-3" size={20} />
          <p className="text-[12.5px] text-slate-500">Opening the calm portal view…</p>
        </div>
      </PortalShell>
    );
  }

  if (error) {
    return (
      <PortalShell>
        <section className="p-6 sm:p-8" style={cardStyle} data-testid="solicitor-portal-error">
          <AlertCircle className="text-amber-500 mb-2" size={18} />
          <h2 className="text-[16px] font-semibold text-slate-900 mb-1">
            This link is no longer valid.
          </h2>
          <p className="text-[13px] text-slate-600 leading-relaxed">
            {error} If you need fresh access, contact the instructing agent —
            they can mint a new calm link for you in a few seconds.
          </p>
        </section>
      </PortalShell>
    );
  }

  if (!view) return null;

  const portalParticipant = view.portal_participant || {};
  const participantLabel =
    PARTICIPANT_LABEL[portalParticipant.type] || "Coordinator";

  return (
    <PortalShell>
      {/* Header */}
      <header className="mb-5">
        <p className={eyebrowStyle} data-testid="solicitor-portal-eyebrow">
          ChainFlow · Solicitor portal
        </p>
        <h1
          className="mt-1 text-[24px] sm:text-[28px] font-semibold tracking-tight text-slate-900 leading-tight"
          data-testid="solicitor-portal-headline"
        >
          {view.headline}
        </h1>
        <p
          className="mt-1 text-[13px] text-slate-600 leading-relaxed"
          data-testid="solicitor-portal-property"
        >
          <Building2 size={12} className="inline-block mr-1 text-slate-400" />
          {view.property?.address}
        </p>
        <p className="mt-2 text-[11.5px] text-slate-500" data-testid="solicitor-portal-identity">
          You're viewing this transaction as{" "}
          <span className="font-semibold text-slate-700">
            {portalParticipant.name || "the named solicitor"}
          </span>{" "}
          · {participantLabel}
        </p>
      </header>

      <Stage stage={view.stage} />

      {/* Acknowledgement CTA — calm reassurance loop */}
      <section className="p-4 sm:p-5 mt-4" style={cardStyle} data-testid="solicitor-portal-ack-card">
        <div className="flex items-start gap-3 flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className={eyebrowStyle}>Calm acknowledgement</p>
            <p className="text-[13.5px] font-semibold text-slate-900 mt-0.5 leading-snug">
              Confirm you've seen the latest update.
            </p>
            <p className="text-[12px] text-slate-500 leading-snug mt-0.5">
              One tap. The instructing agent sees this immediately — saves
              an email round-trip.
            </p>
          </div>
          <button
            type="button"
            onClick={handleAcknowledge}
            disabled={busyAck || acked}
            className="h-10 px-4 rounded-xl text-[12.5px] font-semibold text-white inline-flex items-center justify-center gap-1.5 disabled:opacity-60 self-stretch sm:self-auto"
            style={{
              background: acked
                ? "linear-gradient(135deg, #059669 0%, #10B981 100%)"
                : "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
              boxShadow: "0 12px 26px -12px rgba(99,102,241,0.55)",
            }}
            data-testid="solicitor-portal-ack-button"
          >
            {busyAck ? (
              <Loader2 size={13} className="animate-spin" />
            ) : acked ? (
              <CheckCircle2 size={13} />
            ) : (
              <ShieldCheck size={13} />
            )}
            {acked ? "Acknowledged" : busyAck ? "Recording…" : "Acknowledge update"}
          </button>
        </div>
      </section>

      {/* Outstanding documents */}
      {view.outstanding_documents?.length > 0 ? (
        <section className="p-4 sm:p-5 mt-4" style={cardStyle} data-testid="solicitor-portal-documents">
          <div className="flex items-center gap-1.5 mb-2">
            <FileCheck2 size={11} className="text-violet-600" />
            <p className={eyebrowStyle}>Outstanding documents</p>
          </div>
          <ul className="space-y-2.5">
            {view.outstanding_documents.map((doc) => (
              <li
                key={doc.id}
                className="flex items-start gap-3 flex-col sm:flex-row sm:items-center sm:justify-between border-t first:border-t-0 pt-2.5 first:pt-0 border-slate-100"
                data-testid={`solicitor-portal-doc-${doc.id}`}
              >
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-slate-900 leading-snug">
                    {doc.doc_type || doc.title || "Requested document"}
                  </p>
                  {doc.note ? (
                    <p className="text-[11.5px] text-slate-500 leading-snug mt-0.5">
                      {doc.note}
                    </p>
                  ) : null}
                  {doc.requested_at ? (
                    <p className="text-[10.5px] text-slate-400 mt-0.5">
                      Requested {relativeFromNow(doc.requested_at)}
                    </p>
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={() => handleConfirmDoc(doc)}
                  disabled={docBusyId === doc.id}
                  className="h-9 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60 self-stretch sm:self-auto"
                  style={{
                    border: "1px solid rgba(139,92,246,0.28)",
                    color: "#5B21B6",
                    background:
                      "linear-gradient(135deg, rgba(139,92,246,0.06), rgba(99,102,241,0.03))",
                  }}
                  data-testid={`solicitor-portal-doc-confirm-${doc.id}`}
                >
                  {docBusyId === doc.id ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <FileCheck2 size={12} />
                  )}
                  Mark as ready
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Open blockers (read-only on the portal — visibility only) */}
      {view.open_blockers?.length > 0 ? (
        <section className="p-4 sm:p-5 mt-4" style={cardStyle} data-testid="solicitor-portal-blockers">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertCircle size={11} className="text-amber-600" />
            <p className={eyebrowStyle}>Currently being coordinated</p>
          </div>
          <ul className="space-y-2.5">
            {view.open_blockers.map((b, i) => (
              <li
                key={`${b.title}-${i}`}
                className="border-t first:border-t-0 pt-2.5 first:pt-0 border-slate-100"
              >
                <p className="text-[13px] font-semibold text-slate-900 leading-snug">
                  {b.title}
                </p>
                {b.description ? (
                  <p className="text-[12px] text-slate-600 leading-snug mt-0.5">
                    {b.description}
                  </p>
                ) : null}
                <p className="text-[10.5px] text-slate-400 mt-0.5">
                  Raised {relativeFromNow(b.occurred_at)}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Report a blocker */}
      <section className="p-4 sm:p-5 mt-4" style={cardStyle} data-testid="solicitor-portal-blocker-form">
        <div className="flex items-center gap-1.5 mb-1">
          <Sparkles size={11} className="text-violet-500" />
          <p className={eyebrowStyle}>Flag a coordination item</p>
        </div>
        <p className="text-[12px] text-slate-500 leading-snug mb-2">
          Stuck on something? One calm line goes straight to the instructing
          agent — no emails, no chasing.
        </p>
        <textarea
          rows={2}
          value={blockerText}
          onChange={(e) => setBlockerText(e.target.value.slice(0, 280))}
          placeholder="e.g. Waiting on lender response · expected by Thursday"
          className="w-full text-[13px] text-slate-800 placeholder-slate-400 px-3 py-2.5 rounded-xl border border-slate-200 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100 leading-snug"
          data-testid="solicitor-portal-blocker-input"
        />
        <div className="flex items-center justify-between mt-2 gap-2">
          <p className="text-[10.5px] text-slate-400">{blockerText.length}/280</p>
          <button
            type="button"
            onClick={handleBlocker}
            disabled={busyBlock || blockerText.trim().length < 3}
            className="h-9 px-3 rounded-lg text-[12px] font-semibold text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            style={{
              background:
                "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
            }}
            data-testid="solicitor-portal-blocker-submit"
          >
            {busyBlock ? <Loader2 size={12} className="animate-spin" /> : <ChevronRight size={12} />}
            Send to instructing agent
          </button>
        </div>
      </section>

      {/* Upcoming milestones */}
      {view.milestones?.length > 0 ? (
        <section className="p-4 sm:p-5 mt-4" style={cardStyle} data-testid="solicitor-portal-milestones">
          <div className="flex items-center gap-1.5 mb-2">
            <Clock size={11} className="text-violet-600" />
            <p className={eyebrowStyle}>Upcoming milestones</p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {view.milestones.map((m, i) => (
              <li
                key={`${m.label}-${i}`}
                className="px-3 py-2.5 rounded-xl"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(139,92,246,0.06), rgba(99,102,241,0.02))",
                  border: "1px solid rgba(139,92,246,0.16)",
                }}
              >
                <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">
                  {m.label}
                </p>
                <p className="text-[13.5px] font-semibold text-slate-900 mt-0.5">
                  {formatDate(m.date)}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Participants */}
      {view.participants?.length > 0 ? (
        <section className="p-4 sm:p-5 mt-4" style={cardStyle} data-testid="solicitor-portal-participants">
          <div className="flex items-center gap-1.5 mb-2">
            <ListChecks size={11} className="text-violet-600" />
            <p className={eyebrowStyle}>Transaction coordination</p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {view.participants.map((p, i) => (
              <li
                key={`${p.type}-${i}`}
                className="flex items-baseline justify-between gap-3 py-1 text-[12.5px]"
                data-testid={`solicitor-portal-participant-${p.type}`}
              >
                <span className="text-slate-500 shrink-0">
                  {PARTICIPANT_LABEL[p.type] || p.type}
                </span>
                <span className="text-slate-800 font-medium truncate">
                  {p.name}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Timeline */}
      {view.timeline?.length > 0 ? (
        <section className="p-4 sm:p-5 mt-4" style={cardStyle} data-testid="solicitor-portal-timeline">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles size={11} className="text-violet-500" />
            <p className={eyebrowStyle}>Recent operational updates</p>
          </div>
          <ul className="space-y-2">
            {view.timeline.map((t, i) => (
              <li
                key={`${t.phrase}-${i}`}
                className="flex items-start gap-2.5 text-[12.5px] text-slate-700 leading-snug"
                data-testid="solicitor-portal-timeline-item"
              >
                <span
                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: "#8B5CF6" }}
                />
                <span className="flex-1 min-w-0">
                  <span className="font-medium text-slate-800">{t.phrase}</span>
                  {t.title ? (
                    <span className="text-slate-500"> · {t.title}</span>
                  ) : null}
                  <span className="block text-[10.5px] text-slate-400 mt-0.5">
                    {relativeFromNow(t.occurred_at)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <footer className="mt-6 pb-12 text-center">
        <p className="text-[10.5px] text-slate-400 leading-relaxed">
          You're seeing a calm, scoped operational view. No agency internals,
          no client data outside this transaction.
        </p>
        <p className="text-[10.5px] text-slate-400 leading-relaxed mt-1">
          Coordinated through{" "}
          <span className="font-semibold text-slate-600">ChainFlow</span>.
        </p>
      </footer>
    </PortalShell>
  );
}

function PortalShell({ children }) {
  return (
    <div
      className="min-h-screen w-full"
      style={{
        background:
          "radial-gradient(60% 50% at 30% 0%, rgba(139,92,246,0.10), rgba(255,255,255,0) 60%), #F8FAFC",
      }}
    >
      <div className="mx-auto max-w-2xl px-4 sm:px-6 pt-8 sm:pt-12">
        {children}
      </div>
    </div>
  );
}

function Stage({ stage }) {
  if (!stage) return null;
  return (
    <section
      className="px-4 sm:px-5 py-3.5"
      style={{
        ...cardStyle,
        background:
          "linear-gradient(135deg, rgba(139,92,246,0.10) 0%, rgba(99,102,241,0.06) 100%)",
        border: "1px solid rgba(139,92,246,0.22)",
      }}
      data-testid="solicitor-portal-stage"
    >
      <p className={eyebrowStyle}>Current stage</p>
      <p className="text-[15px] font-semibold text-indigo-950 mt-0.5">
        {stage.label}
      </p>
    </section>
  );
}
